import React, { useEffect } from 'react';
import { OnboardingStatusResponse } from '../types';

interface OnboardingCompleteProps {
  token: string;
  onboardingData: OnboardingStatusResponse;
  onStepComplete: (step: string, data?: Record<string, unknown>) => Promise<void>;
  onComplete: () => Promise<void>;
  error: string | null;
}

/**
 * Completion confirmation step for Fair Manager onboarding
 * Issue #1455 - Step 7 of 7: Success message and redirect to dashboard
 */
const OnboardingComplete: React.FC<OnboardingCompleteProps> = ({
  onboardingData,
  onComplete,
}) => {
  const { fair } = onboardingData;

  useEffect(() => {
    // Trigger completion on mount if not already completed
    if (!onboardingData.onboarding.completed) {
      onComplete().catch((err) => {
        console.error('Failed to complete onboarding:', err);
      });
    }
  }, [onboardingData.onboarding.completed, onComplete]);

  return (
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="text-center">
        {/* Success animation */}
        <div className="mb-6">
          <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100">
            <svg
              className="h-12 w-12 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        {/* Success message */}
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Congratulations! You're All Set
        </h2>
        <p className="text-lg text-gray-600 mb-8">
          Your onboarding is complete. You're now ready to manage <strong>{fair.name}</strong>!
        </p>

        {/* What's next section */}
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-6 mb-8 text-left">
          <h3 className="text-lg font-semibold text-teal-900 mb-4">What You Can Do Now</h3>
          <ul className="space-y-3">
            <li className="flex items-start">
              <svg
                className="h-6 w-6 text-teal-600 mr-3 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <strong className="text-teal-900">Configure Vendor Booths</strong>
                <p className="text-teal-800 text-sm mt-1">
                  Set up booth spaces with pricing, categories, and availability
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <svg
                className="h-6 w-6 text-teal-600 mr-3 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <strong className="text-teal-900">Manage Vendor Reservations</strong>
                <p className="text-teal-800 text-sm mt-1">
                  Review and approve vendor booth reservation requests
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <svg
                className="h-6 w-6 text-teal-600 mr-3 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <strong className="text-teal-900">Track Fair Performance</strong>
                <p className="text-teal-800 text-sm mt-1">
                  Monitor booth reservations, revenue, and vendor participation
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <svg
                className="h-6 w-6 text-teal-600 mr-3 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <strong className="text-teal-900">Manage Waitlists</strong>
                <p className="text-teal-800 text-sm mt-1">
                  Handle vendor waitlists when your fair reaches capacity
                </p>
              </div>
            </li>
          </ul>
        </div>

        {/* Resources section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 text-left">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">Resources & Support</h3>
          <ul className="space-y-2 text-blue-800">
            <li>
              <a
                href="/help/fair-managers"
                className="hover:underline font-medium"
                target="_blank"
                rel="noopener noreferrer"
              >
                📖 Fair Manager Guide
              </a>
              {' - '}Learn best practices and tips
            </li>
            <li>
              <a
                href="/contact"
                className="hover:underline font-medium"
                target="_blank"
                rel="noopener noreferrer"
              >
                💬 Contact Support
              </a>
              {' - '}Get help from our team
            </li>
            <li>
              <a
                href="/help/faq"
                className="hover:underline font-medium"
                target="_blank"
                rel="noopener noreferrer"
              >
                ❓ FAQ
              </a>
              {' - '}Common questions and answers
            </li>
          </ul>
        </div>

        {/* Redirect message */}
        <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-8">
          <p className="text-sm text-gray-600">
            Redirecting you to your Fair Manager Dashboard in a few seconds...
          </p>
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-teal-600 h-2 rounded-full animate-pulse"
                style={{ width: '100%' }}
                role="progressbar"
                aria-label="Redirecting"
              />
            </div>
          </div>
        </div>

        {/* Manual navigation */}
        <div>
          <a
            href="/fairmanager/dashboard"
            className="inline-flex items-center px-6 py-3 bg-teal-600 text-white text-lg font-semibold rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-colors"
          >
            Go to Dashboard Now
            <svg
              className="ml-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </a>
        </div>

        <p className="mt-8 text-sm text-gray-500">
          Welcome to the Local Artist Fairs community! 🎉
        </p>
      </div>
    </div>
  );
};

export default OnboardingComplete;
